// src/App.js
import './App.css';
import './output.css';
import Dashboard from './views/Landing/AIDashboard.jsx';
import HomeView from './views/Home/HomeView.jsx';
import HomeDashboard from './views/Home/HomeDashboard.jsx';
import AppShell from './views/AppShell.jsx';
import Login from './views/Login/Login.jsx';
import Signup from './views/Signup/Signup.jsx';
import ForgotPassword from './views/ForgotPassword/ForgotPassword.jsx';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy.jsx';
import TermsAndCondition from './components/TermsAndCondition/TermsAndCondition.jsx';
import NotFound from './views/NotFound/NotFound.jsx';
import WhatsAppOAuthCallback from './components/Integrations/WhatsAppOAuthCallback.jsx';
import InstagramOAuthCallback from './components/Integrations/InstagramOAuthCallback.jsx';
import ShopifyOAuthCallback from './components/Integrations/ShopifyOAuthCallback.jsx';
import { AuthProvider } from './AuthContext.jsx';
import { AssistantProvider } from './contexts/AssistantContext.jsx';
import { OrganizationProvider } from './contexts/OrganizationContext.jsx';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import client from './apollo-client';
import './i18n/i18n';
import ScrollToTop from './components/ScrollToTop/ScrollToTop.jsx';
import CookieBanner from './components/CookiePolicyBanner/CookiePolicyBanner.jsx';
import PricingPage from './views/Pricing/PricingPage.jsx';
import SupportPage from './views/Support/SupportPage.jsx';
import SettingsLayout from './views/Settings/SettingsLayout.jsx';
import OrganizationLayout from './views/Organizations/OrganizationLayout.jsx';
import UserSettings from './views/Settings/UserSettings.jsx';
import AccountSettings from './views/Settings/AccountSettings.jsx';
import OrgSettings from './views/Settings/OrgSettings.jsx';
import LegacyAccountRedirect from './views/Settings/LegacyAccountRedirect.jsx';
import { useAuth } from './useAuth';
import { WorkflowBuilderView } from './views/WorkflowEditor/index.js';
import { WorkflowsView } from './components/Workflows';
import OrganizationsView from './views/Organizations/OrganizationsView.jsx';
import RequireAuth from './components/RequireAuth/RequireAuth.jsx';
import InitialLoadGate from './components/InitialLoadGate/InitialLoadGate.jsx';
import RedeemView from './views/Redeem/RedeemView.jsx';
import WizardIgView from './views/WizardIg/WizardIgView.jsx';
import RedeemGuideView from './views/Redeem/RedeemGuideView.jsx';
import VerifyEmailView from './views/VerifyEmail/VerifyEmailView.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';

function AppContent() {
  const { isAuthenticated } = useAuth();

  return (
    <>
      <ScrollToTop />
      <CookieBanner />

      <Routes>
        {/* Rutas públicas */}
        <Route path="/Login" element={<Login />} />
        <Route path="/Signup" element={<Signup />} />
        <Route path="/ForgotPassword" element={<ForgotPassword />} />

        {/* Legal: publicas y sin login — la pantalla de consentimiento de
            Google enlaza estas dos URLs y el revisor las abre sin sesion. */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndCondition />} />
        
        {/* Callback de WhatsApp OAuth */}
        <Route path="/integrations/whatsapp/callback" element={<WhatsAppOAuthCallback />} />

        {/* Callback de Instagram Business Login OAuth */}
        <Route path="/integrations/instagram/callback" element={<InstagramOAuthCallback />} />

        {/* Callback de Shopify OAuth */}
        <Route path="/shopify/callback" element={<ShopifyOAuthCallback />} />

        {/* Redeem — canje de templates (publico, sin login) */}
        <Route path="/redeem" element={<RedeemView />} />

        {/* Wizard IG — creación guiada de agente desde un link de Instagram.
            Ruta pública: el propio wizard revisa la sesión y redirige a
            Login/Signup si hace falta, conservando el regreso. */}
        <Route path="/wizard-ig" element={<WizardIgView />} />
        <Route path="/verify-email/:token" element={<VerifyEmailView />} />
        <Route path="/redeem/guide/:channel" element={<RedeemGuideView />} />

        {/* Rutas protegidas */}
        <Route element={<RequireAuth />}>
          {/* Zeno + Home + Dashboard share a persistent Sidebar so switching
              between any rail option doesn't remount the rail (and reload the
              profile photo). Every rail item navigates to one of these three. */}
          {/* Un ErrorBoundary por superficie contiene el crash a esa vista.
              El de index.js queda como última defensa. */}
          <Route element={<ErrorBoundary><AppShell /></ErrorBoundary>}>
            <Route path="/" element={<HomeView />} />
            <Route path="/home" element={<HomeDashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            {/* Ayuda. Va dentro de AppShell porque es una utilidad del rail, no
                una superficie aparte como configuración. */}
            <Route path="/support" element={<SupportPage />} />
            {/* Tarifas. Conserva su URL; lo que desapareció es la carcasa que la
                servía, no la vista. */}
            <Route path="/user/pricing" element={<PricingPage />} />
            {/* La lista de organizaciones sigue siendo un destino: es navegación,
                elegir en cuál trabajas. Administrar una es otra cosa, y tiene su
                propia URL en vez de un modal sin dirección.

                Va dentro del shell porque el cajón móvil la ofrece como destino,
                y fuera de él tocarla dejaba al usuario sin barra de abajo, sin
                cajón y sin hamburguesa: la única salida era el botón de volver. */}
            <Route path="/organizations" element={<OrganizationsView />} />
          </Route>
          <Route path="/dashboard/workflow-editor" element={<ErrorBoundary><WorkflowBuilderView /></ErrorBoundary>} />
          <Route path="/workflow/builder" element={<ErrorBoundary><WorkflowBuilderView /></ErrorBoundary>} />
          <Route path="/WorkflowEditor" element={<ErrorBoundary><WorkflowBuilderView /></ErrorBoundary>} />
          <Route path="/workflows" element={<ErrorBoundary><WorkflowsView embedded={false} /></ErrorBoundary>} />
          {/* /user/:section ya no existe: cada una de sus siete secciones vive
              en su alcance, y las dos que no eran ajustes —tarifas y ayuda—
              tienen pantalla propia. Lo que queda son redirecciones. */}
          <Route path="/user" element={<Navigate to="/u/settings/profile" replace />} />
          <Route path="/user/profile" element={<Navigate to="/u/settings/profile" replace />} />

          {/* Configuración. El alcance va en la URL —y no en estado— para que
              compartir el enlace, recargar y tener dos pestañas abiertas en
              contextos distintos funcionen.

              Las tres familias comparten `SettingsLayout`, que monta el shell una
              sola vez. Con un shell por ruta, moverse entre secciones lo
              desmontaba y volvía a montar: la barra lateral desaparecía y su
              animación de entrada se repetía en cada clic. */}
          <Route element={<ErrorBoundary><SettingsLayout /></ErrorBoundary>}>
            <Route path="/u/settings/:section" element={<UserSettings />} />
            <Route path="/a/:accountId/:section" element={<AccountSettings />} />
            {/* Sin sección: redirige a la suya por defecto dentro del shell,
                para que el redireccionamiento no pinte contenido suelto. */}
            <Route path="/a/:accountId" element={<AccountSettings />} />
          </Route>

          {/* La organización tiene su propia superficie, con el mismo menú pero
              fuera de configuración. Configuración es la sesión —tú y la cuenta
              en la que estás—; a una organización se entra desde su lista y
              puede ser cualquiera de las que perteneces. Mezclar los dos modos
              de direccionar en un menú es lo que lo volvía confuso. */}
          <Route element={<ErrorBoundary><OrganizationLayout /></ErrorBoundary>}>
            <Route path="/o/:orgId/:section" element={<OrgSettings />} />
            <Route path="/o/:orgId" element={<OrgSettings />} />
          </Route>
          <Route path="/u/settings" element={<Navigate to="/u/settings/profile" replace />} />
          <Route path="/u" element={<Navigate to="/u/settings/profile" replace />} />


          {/* Compat. Wallet, Coins y Subscriptions eran tres entradas de menú
              para el mismo dinero; ahora son pestañas de Facturación.
              /user/coins se queda para siempre: es la URL de retorno de los
              pagos ya iniciados, y llega con ?order= que hay que arrastrar. */}
          <Route path="/Profile" element={<Navigate to="/u/settings/profile" replace />} />
          <Route path="/user/settings" element={<Navigate to="/u/settings/security" replace />} />
          <Route path="/user/support" element={<Navigate to="/support" replace />} />
          <Route path="/user/wallet" element={<LegacyAccountRedirect section="billing" />} />
          <Route path="/user/coins" element={<LegacyAccountRedirect section="billing" tab="coins" />} />
          <Route path="/user/subscriptions" element={<LegacyAccountRedirect section="billing" tab="plan" />} />
        </Route>

       
        <Route
          path="*"
          element={
            isAuthenticated
              ? <NotFound />
              : <Navigate to="/Login" replace />
          }
        />
      </Routes>

    
    </>
  );
}

export default function App() {
  return (
    <Router>
      <ApolloProvider client={client}>
        <AuthProvider>
          <OrganizationProvider>
            <AssistantProvider>
              <InitialLoadGate>
                <AppContent />
              </InitialLoadGate>
            </AssistantProvider>
          </OrganizationProvider>
        </AuthProvider>
      </ApolloProvider>
    </Router>
  );
}
